import { useState } from "react";
import {
  X,
  Loader2,
  Pin,
  Archive,
  ArchiveRestore,
  Pencil,
  Trash2,
  Plus,
  MessageSquare,
} from "lucide-react";
import {
  archiveProject,
  restoreProject,
  pinProject,
  unpinProject,
  updateProject,
  deleteProject,
  projectColorHex,
  PROJECT_COLORS,
  Project,
} from "@/lib/api/projects";
import { cn } from "@/lib/utils";

interface Props {
  projects: Project[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onProjectSelect: (id: string) => void;
  onCreateClick: () => void;
}

/**
 * ProjectsPage — full-page projects list (PROJECT_SPEC §3).
 * Grid of project cards with real data (single source of truth = parent).
 * Card menu: open, pin/unpin, archive/restore, rename (inline), delete
 * (with confirm). Export — disabled (future).
 */
export function ProjectsPage({
  projects,
  loading,
  error,
  onRefresh,
  onProjectSelect,
  onCreateClick,
}: Props) {
  const [menuId, setMenuId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const commitRename = async (id: string) => {
    const name = editValue.trim();
    setEditingId(null);
    if (!name) return;
    try {
      await updateProject(id, { name });
      onRefresh();
    } catch {
      /* keep the old name */
    }
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      /* ignore — list state unchanged */
    } finally {
      setBusy(false);
      setMenuId(null);
    }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setBusy(true);
    try {
      await deleteProject(deleteId);
      setDeleteId(null);
      onRefresh();
    } catch {
      setBusy(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-app">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-medium text-primary">Проекты</h1>
            <p className="mt-1.5 text-sm text-secondary max-w-lg">
              Постоянные рабочие пространства: чаты, файлы, инструкции и
              память — в одном месте.
            </p>
          </div>
          <button
            onClick={onCreateClick}
            className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-accent text-accent-contrast text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={16} /> Новый проект
          </button>
        </div>

        {/* States */}
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3 text-tertiary">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Загрузка проектов…</span>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-sm text-secondary">{error}</p>
            <button
              onClick={onRefresh}
              className="mt-3 h-9 px-4 rounded-lg text-sm text-accent hover:bg-hoverbg transition-colors"
            >
              Повторить
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="py-24 flex flex-col items-center gap-3 text-center">
            <MessageSquare size={28} className="text-tertiary" />
            <p className="text-sm text-secondary">Проектов пока нет</p>
            <button
              onClick={onCreateClick}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-accent text-accent-contrast text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Plus size={16} /> Создать первый проект
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map((p) => {
              const archived = p.is_archived;
              return (
                <div
                  key={p.id}
                  className={cn(
                    "relative bg-surface border border-hairline rounded-xl p-4 transition-colors hover:border-strongline",
                    archived && "opacity-70",
                  )}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onProjectSelect(p.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onProjectSelect(p.id);
                      }
                    }}
                    className="cursor-pointer text-left"
                    aria-label={`Открыть проект ${p.name}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="w-8 h-8 rounded-lg bg-hoverbg flex items-center justify-center text-base leading-none shrink-0"
                        style={{ color: projectColorHex(p.color) }}
                        aria-hidden
                      >
                        {p.icon || "◆"}
                      </span>
                      {editingId === p.id ? (
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => void commitRename(p.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void commitRename(p.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 min-w-0 chat-search-input h-8 px-2 text-sm"
                        />
                      ) : (
                        <span className="flex-1 min-w-0 truncate text-sm font-medium text-primary">
                          {p.name}
                        </span>
                      )}
                      {p.is_pinned && (
                        <Pin size={12} className="shrink-0 text-accent" />
                      )}
                    </div>
                    <p className="mt-2.5 text-xs text-secondary line-clamp-2 min-h-[2rem]">
                      {p.description || "Без описания"}
                    </p>
                    <p className="mt-2 text-[11px] text-tertiary">
                      Обновлено {new Date(p.updated_at).toLocaleDateString("ru-RU")}
                      {archived && " · в архиве"}
                    </p>
                  </div>

                  {/* Card menu */}
                  <div className="absolute top-3 right-3">
                    <button
                      onClick={() => setMenuId(menuId === p.id ? null : p.id)}
                      className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
                      aria-label={`Меню проекта ${p.name}`}
                    >
                      <span className="block w-3.5 text-center text-primary text-base leading-none">⋯</span>
                    </button>
                    {menuId === p.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30">
                        <CardMenu
                          label={p.is_pinned ? "Открепить" : "Закрепить"}
                          icon={p.is_pinned ? <UnpinIcon /> : <Pin size={13} />}
                          onClick={() =>
                            void act(() =>
                              p.is_pinned ? unpinProject(p.id) : pinProject(p.id),
                            ).then(() => onRefresh())
                          }
                        />
                        {!archived && (
                          <CardMenu
                            label="В архив"
                            icon={<Archive size={13} />}
                            onClick={() =>
                              void act(() => archiveProject(p.id)).then(() => onRefresh())
                            }
                          />
                        )}
                        {archived && (
                          <CardMenu
                            label="Восстановить"
                            icon={<ArchiveRestore size={13} />}
                            onClick={() =>
                              void act(() => restoreProject(p.id)).then(() => onRefresh())
                            }
                          />
                        )}
                        <CardMenu
                          label="Переименовать"
                          icon={<Pencil size={13} />}
                          onClick={() => {
                            setEditingId(p.id);
                            setEditValue(p.name);
                            setMenuId(null);
                          }}
                        />
                        <CardMenu
                          label="Экспорт"
                          icon={<Loader2 size={13} />}
                          disabled
                        />
                        <div className="border-t border-hairline" />
                        <CardMenu
                          label="Удалить"
                          icon={<Trash2 size={13} />}
                          danger
                          onClick={() => {
                            setDeleteId(p.id);
                            setMenuId(null);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Close menu on outside click */}
      {menuId && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setMenuId(null)}
          aria-hidden
        />
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !busy && setDeleteId(null)}
            aria-hidden
          />
          <div className="relative w-full max-w-sm rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
            <h3 className="text-base font-medium text-primary">Удалить проект?</h3>
            <p className="mt-2 text-sm text-secondary">
              Проект будет удалён. Чаты сохранятся как обычные (непривязанные);
              инструкции и задачи будут удалены без возможности восстановления.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setDeleteId(null)}
                disabled={busy}
                className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void doDelete()}
                disabled={busy}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardMenu({
  label,
  icon,
  onClick,
  danger,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors",
        disabled
          ? "text-disabled cursor-not-allowed"
          : danger
            ? "text-red-400 hover:bg-red-500/10"
            : "text-primary hover:bg-hoverbg",
      )}
    >
      <span className="shrink-0">{icon}</span>
      {label}
    </button>
  );
}

function UnpinIcon() {
  return <Pin size={13} />;
}
